import { Box, Stack, Typography } from '@mui/material';
import { brandingFixture } from '../lib/branding/branding-fixture';

interface PodcastPlayerProps {
  /**
   * Optional audio source URL. Defaults to the operator-supplied
   * placeholder hosted at
   * `s3://dvgup-podcast-public-download-bucket/2025-05-15/podcast/audio/
   * DVGUP_podcast_2025-05-15.mp3`. Once a real per-day episode URL is
   * available, the SPA passes it through.
   */
  readonly src?: string;
  /**
   * Optional episode title. Defaults to the brand-level fallback from
   * `BrandingTokens.copy.podcastSubtitle` so the slot has a real
   * heading even before per-episode metadata lands.
   */
  readonly episodeTitle?: string;
  /**
   * Optional episode subtitle / one-line description. Defaults to a
   * brand-level placeholder.
   */
  readonly episodeDescription?: string;
  /**
   * Optional episode artwork URL. Defaults to the brand's
   * `podcast-tile.svg` asset (Vite serves it from
   * `public/branding/light/podcast-tile.svg`) so the slot looks
   * complete without per-episode artwork.
   */
  readonly artworkUrl?: string;
  /** MIME type for the source. Defaults to `audio/mpeg`. */
  readonly type?: string;
}

const DEFAULT_PLACEHOLDER_AUDIO_URL =
  'https://dvgup-podcast-public-download-bucket.s3.us-east-1.amazonaws.com/2025-05-15/podcast/audio/DVGUP_podcast_2025-05-15.mp3';

/**
 * `<PodcastPlayer>` — looks like a real audio player rather than a
 * generic content card per Requirement 3.1.4.
 *
 * Layout: a square artwork tile that fills the full height of the
 * player on the left, with the "Now playing" overline + episode
 * title + description stacked on the right above the native HTML5
 * `<audio controls>` strip. Brand-tinted gradient background +
 * subtle border, no heavy `<Card>` chrome.
 *
 * Brand styling on the audio control: WebKit/Blink browsers expose
 * `::-webkit-media-controls-*` pseudo-elements that we can't fully
 * style from MUI's `sx`, so we set `colorScheme: 'light'` (matches
 * the SPA's MUI palette) and `accentColor` (brand primary) — those
 * apply to the playhead, mute slider, and time labels in modern
 * Chromium / Safari / Firefox without falling back to a custom
 * controller. When `core-infrastructure` ships the v2
 * `BrandingTokens` with a per-mode dark scheme, this gets a
 * matching dark variant.
 *
 * Native `<audio controls>` carries full keyboard / screen-reader
 * accessibility for free; the wrapper just supplies the surrounding
 * "now playing" chrome (artwork + title + description).
 *
 * _Requirements: Requirement 3.1.4, design §6.2._
 */
export function PodcastPlayer({
  src = DEFAULT_PLACEHOLDER_AUDIO_URL,
  episodeTitle,
  episodeDescription,
  artworkUrl,
  type = 'audio/mpeg',
}: PodcastPlayerProps): JSX.Element {
  const resolvedTitle = episodeTitle ?? brandingFixture.copy.podcastSubtitle;
  const resolvedDescription =
    episodeDescription ?? "Today's episode placeholder — wired to a real feed once hosting lands.";
  const resolvedArtwork = artworkUrl ?? '/branding/dark/podcast-tile.svg';
  const playerHeight = 192;
  return (
    <Box
      data-component="podcast-player"
      sx={{
        borderRadius: 2,
        background:
          'linear-gradient(135deg, rgba(0,106,106,0.10) 0%, rgba(74,99,99,0.06) 100%)',
        border: '1px solid',
        borderColor: 'divider',
        padding: 1.5,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: 2,
        minHeight: playerHeight,
      }}
    >
      <Box
        component="img"
        src={resolvedArtwork}
        alt={`${resolvedTitle} artwork`}
        sx={{
          width: playerHeight - 24, // square; matches the inner content height
          height: playerHeight - 24,
          aspectRatio: '1 / 1',
          objectFit: 'contain',
          borderRadius: 1.5,
          backgroundColor: 'grey.900',
          padding: 1,
          flexShrink: 0,
          boxShadow: 1,
          alignSelf: 'center',
        }}
      />
      <Stack
        spacing={1}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            Now playing
          </Typography>
          <Typography
            variant="h4"
            component="h3"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontWeight: 600,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {resolvedTitle}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              mt: 0.25,
            }}
          >
            {resolvedDescription}
          </Typography>
        </Box>
        <Box
          component="audio"
          controls
          preload="none"
          aria-label={`Listen to ${resolvedTitle}`}
          sx={{
            width: '100%',
            height: 36,
            colorScheme: 'light',
            accentColor: 'primary.main',
            borderRadius: 1,
            '&::-webkit-media-controls-panel': {
              backgroundColor: 'rgba(0, 106, 106, 0.08)',
              borderRadius: 4,
            },
          }}
        >
          <source src={src} type={type} />
        </Box>
      </Stack>
    </Box>
  );
}
